// src/services/mrn.js
//
// The portal's canonical MRN: exactly 9 digits, padded with leading zeros
// ("12345" -> "000012345"). Firebase-free so it can be tested on its own.
//
// Used by the PFT import flow. The older per-page helpers (RawDatabasePage's
// normalizeMrn9, ReceptionPage's leading-zero strip, ...) are left as they are,
// so those pages behave exactly as before.

/**
 * Normalise an MRN as read from a document.
 * Returns { mrn9, raw, issue }:
 *   mrn9  the 9-digit form, or "" when it can't be made safely
 *   raw   the value as printed (trimmed), always kept
 *   issue "" when fine; otherwise why a person must check it:
 *         "missing", "has letters", "more than 9 digits"
 * Separators (spaces, dashes, dots) are ignored. Letters are never dropped
 * silently: "A12345" is not the same patient as "12345".
 */
export const normalizeMrn9 = (value) => {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return { mrn9: "", raw, issue: "missing" };
  if (/[a-z]/i.test(raw)) return { mrn9: "", raw, issue: "has letters" };
  const digits = raw.replace(/\D/g, "");
  if (!digits) return { mrn9: "", raw, issue: "missing" };
  if (digits.length > 9) return { mrn9: "", raw, issue: "more than 9 digits" };
  return { mrn9: digits.padStart(9, "0"), raw, issue: "" };
};

/** Just the 9-digit form ("" when it can't be made). */
export const mrn9 = (value) => normalizeMrn9(value).mrn9;

/** True when two MRNs are the same patient number once normalised. */
export const sameMrn = (a, b) => {
  const x = mrn9(a);
  return !!x && x === mrn9(b);
};
